import { useEffect, useMemo, useRef, useState } from "react";
import { ContentCatalogService } from "@services/contentCatalog/ContentCatalogService";
import AutoGrowTextarea from "@/shared/components/AutoGrowTextarea";

/*
    Authoring an SMS template.

    An SMS is an Ad like any other, so approval, rebranding and the content lock all work
    unchanged. What it carries is one field: the message. That is the whole container type.

    The counter is the part that earns its place. SMS is billed per segment, and a segment is
    not 160 characters of anything: one character outside the basic GSM-7 set forces the
    entire message to UCS-2 and drops every segment from 160 characters to 70. A curly
    apostrophe pasted from a word processor turns a one-segment reminder into three, at three
    times the cost, without changing a visible word. Nothing about the text on screen shows
    that, so it is computed here and shown while the author can still fix it.

    Counted in the browser rather than asked of the server, unlike the test card. This runs on
    every keystroke inside an editor somebody is typing in, and the arithmetic is the same
    arithmetic; the server's answer is the authority only when money is about to be spent.

    Two things are deliberately NOT here:

    The STOP line. Carriers require an opt-out notice and Twilio appends its own handling, so
    an author who deletes it creates a compliance problem that nothing in this screen would
    catch. It is a warning rather than an enforced suffix, because on a follow-up message in
    a conversation the notice is not always required and a forced one wastes a segment.

    Links. A shortened link is what the per-recipient destination becomes at send time, the
    same way the mail piece's QR code does. Typing one here gets it sent verbatim to
    everybody, which is a link nobody can attribute.
*/

interface SmsFormProps {
    ad: { id: number; name?: string | null };
    /** CreateEditorModal owns the content lock — see requestEdit there. */
    requestEdit: (run: (force: boolean) => void | Promise<void>) => void;
}

// The GSM-7 alphabet. Anything outside it forces the whole message to UCS-2.
const GSM = new Set(
    "@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !\"#¤%&'()*+,-./0123456789:;<=>?" +
    "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà",
);
// These are in GSM-7 but cost two characters each: they are sent as an escape plus the
// character. Counting them as one is how a message that says it fits arrives split.
const EXTENDED = new Set("^{}[]~|\\€");

function measure(body: string) {
    const isUnicode = ![...body].every((c) => GSM.has(c) || EXTENDED.has(c));
    const length = isUnicode
        ? [...body].length
        : [...body].reduce((n, c) => n + (EXTENDED.has(c) ? 2 : 1), 0);
    const [single, perPart] = isUnicode ? [70, 67] : [160, 153];
    const segments = length <= single ? 1 : Math.ceil(length / perPart);
    const budget = segments <= 1 ? single : perPart * segments;
    return { isUnicode, length, segments, remaining: Math.max(0, budget - length) };
}

const label: React.CSSProperties = {
    display: "block", fontSize: 11, color: "#6c7585", marginBottom: 3,
};
const input: React.CSSProperties = {
    width: "100%", border: "1px solid #d7dde8", borderRadius: 6, padding: "6px 8px",
    fontSize: 12, color: "#2d3648",
};

export default function SmsForm({ ad, requestEdit }: SmsFormProps) {
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [text, setText] = useState("");
    const ccService = useMemo(() => new ContentCatalogService(), []);
    const textRef = useRef(text);
    textRef.current = text;

    useEffect(() => {
        if (!open || loaded) return;
        ccService.getAd(ad.id, ["content"]).then((loadedAd) => {
            const content = (loadedAd.content ?? {}) as Record<string, any>;
            setText(content.text ?? "");
            setLoaded(true);
        }).catch(() => setLoaded(true));
    }, [open, loaded, ad.id, ccService]);

    const save = async (force: boolean) => {
        const latest = await ccService.getAd(ad.id, ["content"]);
        const current = (latest.content ?? {}) as Record<string, any>;
        await ccService.updateAd(ad.id, {
            content: { ...current, text: textRef.current } as any,
            force_edit: force,
        });
    };

    const stats = measure(text);
    const missingStopNotice = text.trim().length > 0 && !/stop/i.test(text);

    return (
        <div style={{ marginTop: 8 }}>
            <button
                type="button"
                onClick={() => setOpen(!open)}
                style={{
                    background: "none", border: "1px solid #d7dde8", borderRadius: 6,
                    padding: "5px 10px", fontSize: 12, color: "#4f596a", cursor: "pointer",
                    width: "100%", textAlign: "left", display: "flex", alignItems: "center", gap: 6,
                }}
            >
                <span style={{ fontSize: 10, color: "#6c7585" }}>{open ? "v" : ">"}</span>
                Text message
                <span style={{ marginLeft: "auto", fontSize: 10, color: "#94a3b8" }}>
                    {ad.name || `Ad ${ad.id}`}
                </span>
            </button>

            {open && (
                <div style={{
                    border: "1px solid #e1e6ef", borderTop: "none", borderRadius: "0 0 6px 6px",
                    padding: 10, display: "flex", flexDirection: "column", gap: 8, background: "#fff",
                }}>
                    {!loaded ? (
                        <div style={{ fontSize: 12, color: "#94a3b8", textAlign: "center", padding: 8 }}>
                            Loading...
                        </div>
                    ) : (
                        <>
                            <div>
                                <label style={label}>
                                    Message
                                    <span style={{ marginLeft: 6, color: "#94a3b8" }}>
                                        {"{{first_name}}"} and {"{{last_name}}"} are filled in
                                        per recipient.
                                    </span>
                                </label>
                                <AutoGrowTextarea
                                    value={text}
                                    onChange={(e) => setText(e.target.value)}
                                    onBlur={() => requestEdit(save)}
                                    rows={3}
                                    style={input}
                                    aria-label="SMS message"
                                />
                            </div>

                            <div style={{
                                display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
                                fontSize: 11, color: "#6c7585",
                            }}>
                                <span>
                                    {stats.length} characters,{" "}
                                    <strong style={{ color: stats.segments > 1 ? "#b45309" : "#2d3648" }}>
                                        {stats.segments} segment{stats.segments === 1 ? "" : "s"}
                                    </strong>
                                    {stats.remaining > 0 && ` (${stats.remaining} left in this one)`}
                                </span>
                                {stats.isUnicode && (
                                    <span style={{
                                        background: "#fef3c7", color: "#92400e", borderRadius: 4,
                                        padding: "1px 6px",
                                    }}>
                                        Unicode: 70 per segment, not 160
                                    </span>
                                )}
                            </div>

                            {stats.isUnicode && (
                                <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                                    A character outside the basic GSM set, usually a curly quote,
                                    en dash or emoji pasted from a word processor, has pushed this
                                    message to Unicode. Every segment now holds 70 characters
                                    instead of 160. Retyping the punctuation usually fixes it.
                                </div>
                            )}

                            {missingStopNotice && (
                                <div style={{ fontSize: 11, color: "#92400e", lineHeight: 1.5 }}>
                                    No opt-out notice. Carriers expect a first message to say how
                                    to stop, usually &quot;Reply STOP to opt out&quot;. Twilio
                                    honours STOP whether or not it is mentioned, but a campaign
                                    without the notice is one carriers can refuse to deliver.
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
